import {
  type MutableRefObject,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

import { clamp } from '../lib/helpers'
import { getAnimationFinishedStepStates } from '../lib/step-definition'
import {
  type Step,
  type StepState,
  defaultStepStates,
} from '../lib/step-definition'
import { FrameBuilderValidationContext } from '../contexts/validation-context'

import Definitions from './definitions'
import Footer from './footer'
import Previews from './previews'

interface BuilderProps {
  frameViewerRef: MutableRefObject<HTMLCanvasElement | null>
  showExtrasStep: boolean
  showProgressBar: boolean
  layoutRectangle: {
    width?: number
    height?: number
  }
  viewerSize: readonly [number, number] | readonly [null, null]
  isScreenSmallOrLarger?: boolean
  onOpenStep?: (step: Step) => void
  onAddToCart?: () => void
}

const Builder = forwardRef<HTMLDivElement, BuilderProps>(
  (
    {
      frameViewerRef,
      showExtrasStep,
      showProgressBar,
      layoutRectangle,
      viewerSize,
      isScreenSmallOrLarger,
      onOpenStep,
      onAddToCart,
    },
    ref,
  ) => {
    const { frameCompletionPercentage } = useContext(
      FrameBuilderValidationContext,
    )

    const containerRef = useRef<HTMLDivElement | null>(null)

    const [activeStep, setActiveStep] = useState<Step | null>(null)
    const [stepStates, setStepStates] =
      useState<Record<Step, StepState>>(defaultStepStates)

    const configurationContainerSize = useMemo(() => {
      if (
        !isScreenSmallOrLarger ||
        !layoutRectangle.width ||
        !layoutRectangle.height
      ) {
        return [undefined, undefined] as const
      }

      return [undefined, layoutRectangle.width * 0.8] as const
    }, [isScreenSmallOrLarger, layoutRectangle])

    const handlePreviewClick = useCallback(
      (step: Step) => {
        setActiveStep(step)
        setStepStates({
          ...defaultStepStates,
          [step]: 'opening' satisfies StepState,
        })

        // Scrolling animation on mobile
        if (containerRef.current && !isScreenSmallOrLarger) {
          const viewerHeight = viewerSize?.[1] ?? 0

          window.scroll({
            // Leave 100px space at the top for overlay header (the 64px is gallery height)
            top: containerRef.current.offsetTop + viewerHeight + 64 - 100,
            behavior: 'smooth',
          })
        }

        if (onOpenStep) {
          onOpenStep(step)
        }
      },
      [isScreenSmallOrLarger, onOpenStep, viewerSize],
    )
    const handleDefinitionClose = useCallback(() => {
      if (activeStep === null) {
        return
      }

      setActiveStep(null)
      setStepStates({
        ...defaultStepStates,
        [activeStep]: 'closing' satisfies StepState,
      })
    }, [activeStep])
    const handleAnimationFinished = useCallback(() => {
      const newStepStates = getAnimationFinishedStepStates(activeStep)
      setStepStates(newStepStates)
    }, [activeStep])
    const getFrameImage = useCallback(() => {
      if (!frameViewerRef.current) {
        return
      }

      return frameViewerRef.current.toDataURL('image/png')
    }, [frameViewerRef])

    return (
      <div
        ref={ref}
        onTransitionEnd={handleAnimationFinished}
        className="shrink-0 flex flex-col justify-between relative w-full sm:w-[260px] md:w-[280px] lg:w-[320px] xl:w-[360px] pt-1.5"
        style={{
          height: configurationContainerSize[1],
        }}
      >
        {/* Progress bar */}
        <div className="absolute left-0 top-0 w-full h-[6px] bg-gray-400">
          <div
            className="absolute h-full bg-black transition-all duration-100"
            style={{
              width: `${
                showProgressBar ? clamp(frameCompletionPercentage, 0, 100) : 100
              }%`,
            }}
          />
        </div>

        <div className="grow-0 shrink flex overflow-y-auto overflow-x-hidden">
          <Previews
            activeStep={activeStep}
            showExtrasStep={showExtrasStep}
            onPreviewClick={handlePreviewClick}
          />
          <Definitions
            stepStates={stepStates}
            showExtrasStep={showExtrasStep}
            onDefinitionClose={handleDefinitionClose}
            className="grow-0 shrink-0"
          />
        </div>

        <Footer
          getFrameImage={getFrameImage}
          onAddToCart={onAddToCart}
          className="flex-none mt-4"
        />
      </div>
    )
  },
)

Builder.displayName = 'Builder'

export default Builder
